export interface ScrollProgressBarProps {
  /** 0 → 1, lo recorrido del documento. */
  progress: number;
  /** `false` = la persona pidió movimiento reducido. */
  motionOk: boolean;
}

/**
 * La barra fina de progreso, pegada al borde superior de la ventana.
 *
 * ── Es un indicador, no un control ───────────────────────────────────
 * No se puede pulsar ni arrastrar, así que no es un `progressbar` con el
 * que nadie vaya a interactuar: va `aria-hidden`. Quien navega con lector
 * de pantalla no necesita que le narren dos píxeles de degradado en cada
 * scroll, y la información —en qué sección está— ya la da la navegación
 * lateral, que sí tiene botones con nombre.
 *
 * ── Se escala, no se ensancha ────────────────────────────────────────
 * `scaleX` sobre un elemento al 100% en vez de cambiar `width`: el ancho
 * dispara recálculo de maquetación en cada fotograma; la transformación
 * la resuelve el compositor. En una barra que se actualiza con el scroll,
 * la diferencia se nota.
 *
 * ── Movimiento reducido ──────────────────────────────────────────────
 * Sin la transición de suavizado. La barra sigue el desplazamiento, que
 * es movimiento que la persona ya está provocando; lo que se quita es el
 * rebote propio que la hacía moverse por su cuenta después de soltar.
 */
export function ScrollProgressBar({ progress, motionOk }: ScrollProgressBarProps) {
  return (
    <div aria-hidden="true" className="fixed left-0 top-0 z-50 h-0.5 w-full bg-ds-border">
      <div
        className="h-full bg-gradient-to-r from-ds-violet-pop via-ds-violet-pop to-ds-ink-3 will-change-transform"
        style={{
          transform: `scaleX(${progress})`,
          transformOrigin: "left center",
          transition: motionOk ? "transform 0.15s ease-out" : undefined,
          filter: "drop-shadow(0 0 2px rgba(92,78,213,0.35))",
        }}
      />
    </div>
  );
}
